import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Card,
    Checkbox,
    DirectoryTable,
    ErrorAlert,
    InlineError,
    Modal,
    Notice,
    PageHeader,
    TextInput,
    linkStyles,
    useFormatters,
} from '@lumlab/platform-ui';
import { apiRequest } from '@lumlab/platform-product/web';
import { useDirectory } from '@lumlab/platform-admin/web';
import {
    archiveFilePage,
    archiveImport,
    archiveImportInput,
    archiveStorageStatus,
    maxArchiveBytes,
    type ArchiveImport,
} from '../contract/archives.ts';
import { archiveApiPath, archiveFileToken, uploadArchive } from './archive-upload.ts';

const resumeKey = 'lumlab.tenders.archive-import';
const activeStatuses = new Set(['pending', 'uploading', 'completing', 'processing', 'cancelling']);

export function ArchiveImportsPage() {
    const { t } = useTranslation(),
        format = useFormatters();
    const list = useDirectory<ArchiveImport>(archiveApiPath),
        client = useQueryClient();
    const storage = useQuery({
        queryKey: ['archive-storage'],
        queryFn: async () =>
            archiveStorageStatus.parse(await apiRequest('/api/admin/products/tenders/archive-storage')),
    });
    const [selectedId, setSelectedId] = useState<string | null>(() => {
        try {
            return localStorage.getItem(resumeKey);
        } catch {
            return null;
        }
    });
    const [creating, setCreating] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const selected = useQuery({
        queryKey: ['archive-import', selectedId],
        enabled: !!selectedId,
        queryFn: async () => archiveImport.parse(await apiRequest(`${archiveApiPath}/${selectedId}`)),
        refetchInterval: (query) => (activeStatuses.has(query.state.data?.status ?? '') ? 10_000 : false),
    });
    useEffect(() => {
        if (selected.data && !activeStatuses.has(selected.data.status))
            void client.invalidateQueries({ queryKey: ['admin', archiveApiPath] });
    }, [selected.data, client]);
    const choose = (id: string, file: File | null = null) => {
        setSelectedId(id);
        setSelectedFile(file);
        try {
            localStorage.setItem(resumeKey, id);
        } catch {
            /* Resume remains available in the directory. */
        }
    };
    return (
        <>
            <PageHeader title={t('tenders.archives.title')} description={t('tenders.archives.description')} />
            {(storage.error || list.error) && <ErrorAlert error={storage.error ?? list.error} />}
            {storage.data && !storage.data.available && (
                <Notice tone="warning">{t('tenders.archives.storageUnavailable')}</Notice>
            )}
            <Button className="self-end" disabled={!storage.data?.available} onClick={() => setCreating(true)}>
                {t('tenders.archives.add')}
            </Button>
            <DirectoryTable
                items={list.data?.items ?? []}
                total={list.data?.total ?? 0}
                loading={list.isPending}
                error={list.error}
                search={list.search}
                onSearchChange={list.setSearch}
                page={list.page}
                pageSize={list.pageSize}
                onPageChange={list.setPage}
                columns={[
                    { key: 'title', label: t('tenders.archives.subject'), render: (item) => item.title },
                    {
                        key: 'date',
                        label: t('tenders.archives.importedAt'),
                        render: (item) => format.shortDate(new Date(item.createdAt)),
                    },
                    {
                        key: 'size',
                        label: t('tenders.archives.size'),
                        render: (item) =>
                            t('tenders.archives.mebibytes', { value: format.number(item.sizeBytes / 1024 ** 2) }),
                    },
                    {
                        key: 'status',
                        label: t('tenders.archives.state'),
                        render: (item) => t(`tenders.archives.status.${item.status}`),
                    },
                    {
                        key: 'actions',
                        label: t('platform.admin.actions'),
                        render: (item) => (
                            <Button size="xs" variant="light" onClick={() => choose(item.id)}>
                                {t('tenders.archives.open')}
                            </Button>
                        ),
                    },
                ]}
            />
            {selected.error && <ErrorAlert error={selected.error} />}
            {selected.data && (
                <ArchiveImportDetail
                    key={selected.data.id}
                    item={selected.data}
                    initialFile={selectedFile}
                    refresh={() => {
                        void selected.refetch();
                        void client.invalidateQueries({ queryKey: ['admin', archiveApiPath] });
                    }}
                />
            )}
            <Modal opened={creating} onClose={() => setCreating(false)} title={t('tenders.archives.add')}>
                <ArchiveImportForm
                    onCreated={(id, file) => {
                        setCreating(false);
                        choose(id, file);
                        void client.invalidateQueries({ queryKey: ['admin', archiveApiPath] });
                    }}
                />
            </Modal>
        </>
    );
}

function ArchiveImportForm({ onCreated }: { onCreated: (id: string, file: File) => void }) {
    const { t } = useTranslation();
    const [fields, setFields] = useState({ title: '', noticeUrl: '', downloadUrl: '', publishedOn: '' });
    const [file, setFile] = useState<File | null>(null),
        [confirmed, setConfirmed] = useState(false),
        [invalid, setInvalid] = useState(false);
    const [id] = useState(() => crypto.randomUUID());
    const create = useMutation({
        mutationFn: async () => {
            if (!file) throw new Error('archive_file_required');
            const input = archiveImportInput.parse({
                ...fields,
                id,
                fileName: file.name,
                sizeBytes: file.size,
                fileToken: await archiveFileToken(file),
                publicSourceConfirmed: confirmed,
            });
            return archiveImport.parse(await apiRequest(archiveApiPath, { method: 'POST', body: input }));
        },
        onSuccess: (item) => {
            if (file) onCreated(item.id, file);
        },
    });
    const field = (key: keyof typeof fields) => ({
        value: fields[key],
        onChange: (event: ChangeEvent<HTMLInputElement>) => setFields({ ...fields, [key]: event.target.value }),
    });
    return (
        <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
                event.preventDefault();
                setInvalid(!file || !confirmed);
                if (file && confirmed) create.mutate();
            }}
        >
            <TextInput required maxLength={500} label={t('tenders.archives.subject')} {...field('title')} />
            <TextInput required type="url" label={t('tenders.archives.noticeUrl')} {...field('noticeUrl')} />
            <TextInput required type="url" label={t('tenders.archives.downloadUrl')} {...field('downloadUrl')} />
            <TextInput required type="date" label={t('tenders.archives.publishedOn')} {...field('publishedOn')} />
            <TextInput
                required
                type="file"
                accept=".zip,application/zip"
                label={t('tenders.archives.file')}
                description={t('tenders.archives.fileHint')}
                onChange={(event) => {
                    const selected = event.target.files?.[0] ?? null;
                    setFile(selected);
                    setInvalid(!!selected && selected.size > maxArchiveBytes);
                }}
            />
            <Checkbox
                required
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
                label={t('tenders.archives.publicConfirmation')}
            />
            {invalid && <InlineError>{t('tenders.archives.invalidInput')}</InlineError>}
            {create.error && <InlineError>{t('tenders.archives.createFailed')}</InlineError>}
            <Button type="submit" loading={create.isPending} disabled={!!file && file.size > maxArchiveBytes}>
                {t('tenders.archives.create')}
            </Button>
        </form>
    );
}

function ArchiveImportDetail({
    item,
    refresh,
    initialFile,
}: {
    item: ArchiveImport;
    refresh: () => void;
    initialFile: File | null;
}) {
    const { t } = useTranslation(),
        format = useFormatters();
    const [file, setFile] = useState<File | null>(initialFile),
        [progress, setProgress] = useState(0),
        [wrongFile, setWrongFile] = useState(false),
        [page, setPage] = useState(1);
    const controller = useRef<AbortController | null>(null);
    const [paused, setPaused] = useState(false);
    useEffect(() => () => controller.current?.abort(), []);
    const transfer = useMutation({
        mutationFn: async () => {
            if (!file) return;
            controller.current = new AbortController();
            setWrongFile(false);
            setPaused(false);
            if ((await archiveFileToken(file)) !== item.fileToken) {
                setWrongFile(true);
                return;
            }
            await uploadArchive(file, item, { signal: controller.current.signal, onProgress: setProgress });
        },
        onSettled: refresh,
    });
    const cancel = useMutation({
        mutationFn: () => {
            controller.current?.abort();
            return apiRequest(`${archiveApiPath}/${item.id}/cancel`, { method: 'POST' });
        },
        onSuccess: refresh,
    });
    const download = useMutation({
        mutationFn: async () => {
            const result = await apiRequest<{ url: string }>(`${archiveApiPath}/${item.id}/download`);
            window.location.assign(result.url);
        },
    });
    const ready = item.status === 'ready' || item.status === 'duplicate';
    const files = useQuery({
        queryKey: ['archive-files', item.id, page],
        enabled: ready,
        queryFn: async () => archiveFilePage.parse(await apiRequest(`${archiveApiPath}/${item.id}/files?page=${page}`)),
    });
    return (
        <Card className="flex flex-col gap-4" role="region" aria-label={t('tenders.archives.details')}>
            <h2 className="text-xl font-bold">{item.title}</h2>
            <p role="status">{t(`tenders.archives.status.${item.status}`)}</p>
            <p className="text-sm text-muted">
                {t('tenders.archives.metadata', {
                    date: format.calendarDate(item.publishedOn),
                    imported: format.dateTime(new Date(item.createdAt)),
                })}
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
                <a href={item.noticeUrl} target="_blank" rel="noreferrer" className={linkStyles}>
                    {t('tenders.archives.noticeUrl')}
                </a>
                <a href={item.downloadUrl} target="_blank" rel="noreferrer" className={linkStyles}>
                    {t('tenders.archives.downloadUrl')}
                </a>
            </div>
            {item.errorCode && (
                <InlineError>
                    {t(`tenders.archives.errors.${item.errorCode}`, {
                        defaultValue: t('tenders.archives.processingFailed'),
                    })}
                </InlineError>
            )}
            {item.status === 'uploading' && (
                <>
                    <TextInput
                        type="file"
                        accept=".zip,application/zip"
                        label={t('tenders.archives.resumeFile')}
                        description={item.fileName}
                        disabled={transfer.isPending}
                        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                    />
                    <progress
                        className="h-2 w-full"
                        max={item.sizeBytes}
                        value={progress}
                        aria-label={t('tenders.archives.progress')}
                    />
                    <p className="text-sm">
                        {t('tenders.archives.progressBytes', {
                            done: format.number(progress / 1024 ** 2),
                            total: format.number(item.sizeBytes / 1024 ** 2),
                        })}
                    </p>
                    {wrongFile && <InlineError>{t('tenders.archives.wrongFile')}</InlineError>}
                    {transfer.error && !paused && <InlineError>{t('tenders.archives.transferFailed')}</InlineError>}
                    <div className="flex flex-wrap gap-3">
                        <Button disabled={!file || transfer.isPending} onClick={() => transfer.mutate()}>
                            {t('tenders.archives.resume')}
                        </Button>
                        <Button
                            variant="default"
                            disabled={!transfer.isPending}
                            onClick={() => {
                                setPaused(true);
                                controller.current?.abort();
                            }}
                        >
                            {t('tenders.archives.pause')}
                        </Button>
                    </div>
                </>
            )}
            {ready && (
                <>
                    <p className="text-sm">{t('tenders.archives.fileCount', { count: item.fileCount })}</p>
                    <p className="font-mono text-xs break-all text-muted">{item.fingerprint}</p>
                    <Button loading={download.isPending} onClick={() => download.mutate()}>
                        {t('tenders.archives.download')}
                    </Button>
                    {(download.error || files.error) && <ErrorAlert error={download.error ?? files.error} />}
                    <ul className="divide-y divide-line text-sm">
                        {files.data?.files.map((entry) => (
                            <li key={entry.path} className="flex justify-between gap-4 py-2">
                                <span className="min-w-0 break-all">{entry.path}</span>
                                <span className="whitespace-nowrap">
                                    {t('tenders.archives.mebibytes', {
                                        value: format.number(entry.sizeBytes / 1024 ** 2),
                                    })}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <div className="flex gap-3">
                        <Button variant="default" size="xs" disabled={page === 1} onClick={() => setPage(page - 1)}>
                            {t('tenders.archives.previous')}
                        </Button>
                        <Button
                            variant="default"
                            size="xs"
                            disabled={page * 100 >= (files.data?.total ?? 0)}
                            onClick={() => setPage(page + 1)}
                        >
                            {t('tenders.archives.next')}
                        </Button>
                    </div>
                </>
            )}
            {!ready && item.status !== 'cancelled' && (
                <Button variant="default" loading={cancel.isPending} onClick={() => cancel.mutate()}>
                    {t('tenders.archives.cancel')}
                </Button>
            )}
            {cancel.error && <ErrorAlert error={cancel.error} />}
        </Card>
    );
}
